// ✅ src/pages/_shared/FactureFromDevisBase.jsx
import React, { useEffect, useMemo, useRef, useState, useContext, useCallback } from "react";
import UnsavedDialog from "../../components/UnsavedDialog";
import { useSearchParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../axiosInstance";
import FooterApercu from "../../components/FooterApercu";
import { SocieteContext } from "../../context/SocieteContext";
import BoutonOnglet from "../../components/BoutonOnglet";
import BlocTotaux from "../../components/BlocTotaux";
import DOMPurify from "dompurify";
import {
  hydrateProtectedHtml,
  revokeAllProtectedUrls,
  stableSigFromSociete,
} from "../../utils/protectedFiles";
import { buildPdfBlobWithLastFooterAndPages } from "../../utils/pdf";
import { notify } from "../../ui/notify";

/* ---------- helpers ---------- */
const safeJSON = (x, fb = {}) => {
  try {
    return typeof x === "string" ? JSON.parse(x) : x ?? fb;
  } catch {
    return fb;
  }
};
const r2 = (n) => Number((Number(n || 0)).toFixed(2));
const fmt2 = (n) =>
  new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n || 0));
const toCurrency = (v) => fmt2(v) + " €";
const pct = (n) =>
  new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(n || 0));
const totalLigne = (l) =>
  parseFloat(l.quantite ?? l.qte ?? 0) * parseFloat(l.prixUnitaire ?? l.pu ?? 0) || 0;
const pxNum = (n, fb) => (Number.isFinite(Number(n)) ? Number(n) : fb);

const addDaysISO = (iso, days) => {
  const d = new Date(iso);
  if (!Number.isNaN(d.getTime())) d.setDate(d.getDate() + Number(days || 0));
  return d.toISOString().slice(0, 10);
};
const endOfMonthISO = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  d.setMonth(d.getMonth() + 1, 0);
  return d.toISOString().slice(0, 10);
};
const parsePaymentTerms = (html) => {
  const text = String(html || "").replace(/<[^>]+>/g, " ");
  const aReception = /à\s*r[eé]ception/i.test(text);
  const finDeMois = /fin\s*de\s*mois/i.test(text);
  const mDays = text.match(/(\d{1,3})\s*jours?/i);
  const days = mDays ? Math.min(365, parseInt(mDays[1], 10)) : 30;
  return { aReception, finDeMois, days };
};
const computeDueDateISO = (issueISO, terms) => {
  if (terms?.aReception) return issueISO;
  if (terms?.finDeMois) return addDaysISO(endOfMonthISO(issueISO), terms.days || 0);
  return addDaysISO(issueISO, terms?.days || 30);
};

const toYYYYMMDD = (raw) => {
  const d = raw ? new Date(raw) : new Date();
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear(),
    m = String(d.getMonth() + 1).padStart(2, "0"),
    day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const dFR = (raw) => {
  if (!raw) return "-";
  const s =
    typeof raw === "string" ? raw.slice(0, 10) : new Date(raw).toISOString().slice(0, 10);
  const [y, m, d] = s.split("-");
  return `${d}/${m}/${y}`;
};

/* Adresse client (ligne + CP + ville) */
const splitClientAddr = (obj = {}) => {
  const addrRaw =
    (obj.client_adresse ||
      obj.numero_voie ||
      obj.voie ||
      obj.rue ||
      obj.adresse ||
      "").trim();
  const m = addrRaw.match(/^(.*?)(?:,\s*)?(\d{4,5})\s+(.+)$/i);
  const ligne1 = (m ? m[1] : addrRaw).trim();
  const cp = (obj.code_postal || obj.cp || obj.postal_code || (m ? m[2] : "")).trim();
  const ville = (obj.ville || obj.commune || obj.city || (m ? m[3] : "")).trim();
  return { ligne1, cp, ville };
};

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}
// ===== Hook commun 'quitter sans enregistrer' =====
function useUnsavedChanges({ isDirty, onSave, navigate, askConfirm }) {
  useEffect(() => {
    const handler = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const confirmLeave = useCallback(async (targetHrefOrFn) => {
    if (!isDirty) {
      if (typeof targetHrefOrFn === "function") return targetHrefOrFn();
      if (typeof targetHrefOrFn === "string") return (window.location.href = targetHrefOrFn);
      return;
    }
    const action = askConfirm ? await askConfirm() : "cancel";
    if (action === "leave") {
      if (typeof targetHrefOrFn === "function") return targetHrefOrFn();
      if (typeof targetHrefOrFn === "string") return (window.location.href = targetHrefOrFn);
      return;
    }
    if (action === "save" && typeof onSave === "function") {
      try {
        await onSave();
        if (typeof targetHrefOrFn === "function") return targetHrefOrFn();
        if (typeof targetHrefOrFn === "string") return (window.location.href = targetHrefOrFn);
      } catch (e) {
        console.warn("Échec enregistrement avant de quitter:", e);
      }
    }
  }, [isDirty, onSave, askConfirm]);

  // back/forward
  useEffect(() => {
    const GUARD_FLAG = "__GUARD__";
    const initial = { ...(history.state || {}), [GUARD_FLAG]: true };
    try { history.replaceState(initial, ""); } catch { }
    try { history.pushState({ ...initial }, ""); } catch { }
    const onPop = () => {
      if (!isDirty) return;
      try { history.pushState({ ...(history.state || {}), [GUARD_FLAG]: true }, ""); } catch { }
      confirmLeave(() => history.back());
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [isDirty, confirmLeave]);

  // Interception des <a> internes
  useEffect(() => {
    const onClick = (e) => {
      if (!isDirty) return;
      let el = e.target;
      while (el && el !== document.body) {
        if (el.tagName === "A" && el.href) break;
        el = el.parentElement;
      }
      if (!el || el.tagName !== "A") return;
      const href = el.getAttribute("href");
      const target = el.getAttribute("target");
      if (!href || href.startsWith("#") || target === "_blank") return;
      const url = new URL(el.href);
      if (url.origin !== window.location.origin) return;
      e.preventDefault();
      e.stopPropagation();
      confirmLeave(() => {
        if (navigate) return navigate(url.pathname + url.search + url.hash);
        window.location.href = el.href;
      });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [isDirty, confirmLeave, navigate]);

  const safeNavigate = useCallback((to) => {
    confirmLeave(() => (navigate ? navigate(to) : (window.location.href = to)));
  }, [confirmLeave, navigate]);

  return { safeNavigate, confirmLeave };
}

// === clone d'ActionsEmailDevis, adapté pour factures ===
function ActionsEmailFactureLikeDevis({ factureMeta, pdfElRef, footerElRef, kind = "partielle" }) {
  // factureMeta: { id, numero, client_email, societe_nom }
  const [open, setOpen] = React.useState(false);

  // destinataires
  const [to, setTo] = React.useState(factureMeta?.client_email || "");
  const [cc, setCc] = React.useState("");

  // modèles (fallback si l’API n’en renvoie pas)
  const [tpls, setTpls] = React.useState({
    initial: {
      subject: "Votre facture {{numero}}",
      body: "Bonjour,\nVeuillez trouver ci-joint votre facture {{numero}}.\n\nCordialement,\n{{societe_nom}}",
    },
    relance: {
      subject: "Relance de paiement – facture {{numero}}",
      body: "Bonjour,\nSauf erreur de notre part, la facture {{numero}} reste en attente.\n\nMerci de votre règlement,\n{{societe_nom}}",
    },
  });

  // sujet/corps (éditables)
  const [subject, setSubject] = React.useState("");
  const [message, setMessage] = React.useState("");

  // mode actif
  const [mode, setMode] = React.useState("initial"); // "initial" | "relance"

  // pièce jointe
  const [attachPdf, setAttachPdf] = React.useState(true);

  // historique (persistant UI + serveur si dispo)
  const storageKey = `mailHistory:facture:${kind}:${factureMeta?.id || "temp"}`;
  const [history, setHistory] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || "[]"); } catch { return []; }
  });
  const [banner, setBanner] = React.useState(null);

  const values = React.useMemo(() => {
    const numero = factureMeta?.numero || "";
    const societe_nom = factureMeta?.societe_nom || "FlowDevis";
    return { numero, societe_nom };
  }, [factureMeta?.numero, factureMeta?.societe_nom]);

  const renderTpl = (s = "", v = {}) =>
    String(s).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => v[k] ?? "");

  const fmtDateTime = (d) => {
    try { return new Intl.DateTimeFormat("fr-FR", { dateStyle: "long", timeStyle: "short" }).format(d); }
    catch { return d?.toLocaleString?.() || String(d); }
  };
  const emailLooksValid = (s = "") => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s).trim());

  // ---- quel template utiliser (côté EmailTemplates.jsx) ----
  const templateIdForInitial = React.useMemo(() => {
    const k = String(kind).toLowerCase();
    if (k === "acompte") return "facture_acompte";
    if (k === "partielle") return "facture_partielle";
    if (k === "finale") return "facture_finale";
    return "facture";
  }, [kind]);

  // charge les templates
  async function fetchTemplates() {
    try {
      const r = await axiosInstance.get("/email-templates");
      const d = r?.data || {};
      const get = (id) => {
        const t = d[id] || {};
        return { subject: t.subject || "", body: t.html || "" };
      };
      const initial = get(templateIdForInitial);
      const relance = get("relance_impaye");

      // fallbacks
      if (!initial.subject && !initial.body) {
        initial.subject = `Votre facture {{numero}} – {{societe_nom}}`;
        initial.body = `Bonjour,\nVeuillez trouver ci-joint votre facture {{numero}}.\n\nCordialement,\n{{societe_nom}}`;
      }
      if (!relance.subject && !relance.body) {
        relance.subject = `Relance de paiement – facture {{numero}}`;
        relance.body = `Bonjour,\nSauf erreur de notre part, la facture {{numero}} reste en attente.\n\nMerci de votre règlement,\n{{societe_nom}}`;
      }
      return { initial, relance };
    } catch {
      return null;
    }
  }

  // ouverture popup → charge modèles + historique
  React.useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const fromApi = await fetchTemplates();
        if (fromApi?.initial || fromApi?.relance) {
          setTpls((o) => ({ ...o, ...fromApi }));
        }
      } finally {
        const tpl = mode === "relance" ? tpls.relance : tpls.initial;
        setSubject(renderTpl(tpl.subject, values));
        setMessage(renderTpl(tpl.body, values));
      }

      try {
        let fromServer = [];
        if (Number(factureMeta?.id) > 0) {
          try {
            const r = await axiosInstance.get("/emails/history", {
              params: { type: "facture", id: Number(factureMeta?.id), kind },
            });
            fromServer = Array.isArray(r?.data) ? r.data : [];
          } catch (e) {
            // Silence les 404 (endpoint non implémenté côté back)
            if (e?.response?.status !== 404) console.warn("[emails/history]", e);
          }
        }
        const merged = [...fromServer, ...history].sort((a, b) => new Date(b.ts) - new Date(a.ts));
        setHistory(merged.slice(0, 100));
        try { localStorage.setItem(storageKey, JSON.stringify(merged.slice(0, 100))); } catch { }
      } catch {
        // pas d’API : on garde localStorage
      }

      setBanner(null);
      setTo(factureMeta?.client_email || "");
    })();
  }, [open]);

  // change onglet -> recharge sujet/corps depuis modèle
  React.useEffect(() => {
    if (!open) return;
    const tpl = mode === "relance" ? tpls.relance : tpls.initial;
    setSubject(renderTpl(tpl.subject, values));
    setMessage(renderTpl(tpl.body, values));
  }, [mode, tpls.initial.subject, tpls.initial.body, tpls.relance.subject, tpls.relance.body]);


  async function buildPdfAttachment() {
    if (!attachPdf) return null;
    const rootEl = pdfElRef?.current ?? document.querySelector(".a4-page");
    const fEl = footerElRef?.current ?? document.querySelector(".a4-footer");
    if (!rootEl) throw new Error("a4-page introuvable");

    document.body.classList.add("static-pdf");
    rootEl.classList.add("pdf-export");
    await new Promise((r) => setTimeout(r, 20));

    const baseName =
      kind === "acompte" ? "Facture-Acompte" :
        kind === "partielle" ? "Facture-Partielle" :
          kind === "finale" ? "Facture-Finale" : "Facture";
    const safeNum = (factureMeta?.numero || `${Date.now()}`).toString().replace(/[^\w.-]+/g, "_");
    const filename = `${baseName}-${safeNum}.pdf`;

    const blob = await buildPdfBlobWithLastFooterAndPages(rootEl, fEl, { filename });
    const contentBase64 = await blobToBase64(blob);

    rootEl.classList.remove("pdf-export");
    document.body.classList.remove("static-pdf");

    return { filename, contentBase64 };
  }

  // envoi
 async function send() {
  try {
    const email = String(to || "").trim();
    const ccStr = String(cc || "").trim();
    if (!emailLooksValid(email)) {
      notify.warning({
        title: "Adresse e-mail invalide",
        message: "Renseigne une adresse valide dans « À ».",
      });
      return;
    }

    // si l’utilisateur a tout effacé, retomber sur le modèle
    const tpl = mode === "relance" ? tpls.relance : tpls.initial;
    const subj = subject?.trim() || renderTpl(tpl.subject, values);
    const body = message?.trim() || renderTpl(tpl.body, values);

    notify.info({ title: "Préparation", message: "Génération du PDF…" });
    const attachment = await buildPdfAttachment();

    // type API en fonction de kind
    const kindStr = String(kind || "").toLowerCase();
    const apiType =
      kindStr === "acompte"
        ? "facture_acompte"
        : kindStr === "partielle"
        ? "facture_partielle"
        : kindStr === "finale"
        ? "facture_finale"
        : "facture";

    await axiosInstance.post("/emails/send-document", {
      type: apiType,                 
      id: Number(factureMeta?.id),
      kind: kindStr,                
      to: email,
      cc: ccStr || undefined,
      subject: subj,
      message: body,
      attachment,
      meta: { mode },                // "initial" | "relance"
    });

  


      const ts = new Date();
      setBanner(`✉️ ${mode === "relance" ? "Relance envoyée" : "E-mail envoyé"} le ${fmtDateTime(ts)}.`);

      const entry = { ts: ts.toISOString(), mode, to: email, cc: ccStr || "", subject: subj };
      const next = [entry, ...history].slice(0, 100);
      setHistory(next);
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { }

      try {
        if (Number(factureMeta?.id) > 0) {
          await axiosInstance.post("/emails/log", { type: "facture", id: Number(factureMeta?.id), kind, entry });
        }
      } catch (e) {
        if (e?.response?.status !== 404) console.warn("[emails/log]", e);
      }


      notify.success({ title: "E-mail envoyé", message: `Facture ${values.numero} envoyée à ${email}.` });
    } catch (err) {
      console.error("[send-facture-email]", err);
      notify.error({ title: "Échec de l’envoi", message: "Impossible d’envoyer l’e-mail. Réessaie dans un instant." });
    } finally {
      document.body.classList.remove("static-pdf");
      pdfElRef?.current?.classList?.remove?.("pdf-export");
    }
  }

  // libellés identiques à AfficherDevis mais “Facture”
  const sendBtnLabel = mode === "relance" ? "Envoyer une relance" : "Envoyer la facture";

  return (
    <>
      {/* même bouton que AfficherDevis */}
      <BoutonOnglet actif={false} onClick={() => setOpen(true)}>Envoyer par e-mail</BoutonOnglet>

      {open && (
        <div className="fixed inset-0 z-[2000] grid place-items-center bg-sky-800/40">          {/* popup élargie identique */}
          <div className="bg-white rounded-xl shadow-xl p-5 w-[900px] max-w-[95vw]">
            {/* Historique */}
            <div className="mb-4 rounded bg-slate-50 border border-slate-200 p-2 text-xs text-slate-700 max-h-24 overflow-auto">
              <div className="font-semibold mb-1">
                Historique des envois – {values.numero} {kind ? `(${kind})` : ""}
              </div>
              {history.length === 0 ? (
                <div>Aucun envoi enregistré.</div>
              ) : (
                history.map((h, i) => (
                  <div key={i} className="flex justify-between gap-3 py-0.5">
                    <span>{new Date(h.ts).toLocaleString()}</span>
                    <span>{h.mode === "relance" ? "Relance" : "Initial"} → {h.to}{h.cc ? ` (Cc: ${h.cc})` : ""}</span>
                    <span className="truncate max-w-[45%]" title={h.subject}>{h.subject}</span>
                  </div>
                ))
              )}
            </div>

            {/* Bandeau confirmation */}
            {banner && (
              <div className="mb-4 rounded-md border border-green-300 bg-green-50 text-green-800 px-3 py-2 text-sm">
                {banner}
              </div>
            )}

            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                Envoyer la facture {kind === "acompte" ? "d’acompte" : kind === "partielle" ? "partielle" : kind === "finale" ? "finale" : ""}
              </h3>

              {/* switch de mode (même style) */}
              <div className="inline-flex rounded-full bg-slate-100 p-1 text-sm">
                <button
                  type="button"
                  className={`px-3 py-1 rounded-full ${mode === "initial" ? "bg-white shadow" : "opacity-70"}`}
                  onClick={() => setMode("initial")}
                >
                  Facture initiale
                </button>
                <button
                  type="button"
                  className={`px-3 py-1 rounded-full ${mode === "relance" ? "bg-white shadow" : "opacity-70"}`}
                  onClick={() => setMode("relance")}
                >
                  Relance facture
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {/* Édition */}
              <div>
                <label className="block text-sm mb-1">À</label>
                <input className="w-full border rounded px-3 py-2 mb-3" value={to} onChange={(e) => setTo(e.target.value)} />

                <label className="block text-sm mb-1">Cc (optionnel)</label>
                <input className="w-full border rounded px-3 py-2 mb-3" value={cc} onChange={(e) => setCc(e.target.value)} />

                <label className="block text-sm mb-1">Objet</label>
                <input className="w-full border rounded px-3 py-2 mb-3" value={subject} onChange={(e) => setSubject(e.target.value)} />

                <label className="block text-sm mb-1">Message</label>
                <textarea className="w-full border rounded px-3 py-2 h-48" value={message} onChange={(e) => setMessage(e.target.value)} />

                <label className="inline-flex items-center gap-2 mt-3">
                  <input type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} />
                  Joindre le PDF
                </label>
              </div>

              {/* Aperçu */}
              <div className="border rounded p-3 bg-slate-50">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                  Modèle utilisé : {mode === "relance" ? "Relance facture" : "Facture initiale"} {kind ? `(${kind})` : ""}
                </div>
                <div className="text-sm font-semibold mb-2">
                  {renderTpl((mode === "relance" ? tpls.relance.subject : tpls.initial.subject), values)}
                </div>
                <pre className="text-xs whitespace-pre-wrap leading-5">
                  {renderTpl((mode === "relance" ? tpls.relance.body : tpls.initial.body), values)}
                </pre>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 justify-end mt-5">
              <button type="button" className="px-3 py-2 border rounded" onClick={() => setOpen(false)}>Fermer</button>
              <button type="button" className="px-4 py-2 bg-sky-700 text-white rounded" onClick={send}>
                {sendBtnLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ---------- composant principal ---------- */
export default function FactureFromDevisBase({ type }) {
  const navigate = useNavigate();
  const { infosSociete } = useContext(SocieteContext);
  const [searchParams] = useSearchParams();
  const [leaveGuard, setLeaveGuard] = useState({ open: false, resolve: null });
  const askConfirm = useCallback(() => new Promise((resolve) => {
    setLeaveGuard({ open: true, resolve }); // resolve("leave" | "save" | "cancel")
  }), []);
  const closeLeaveGuard = useCallback((result = "cancel") => {
    setLeaveGuard((st) => {
      st?.resolve?.(result);
      return { open: false, resolve: null };
    });
  }, []);
  // ✅ clé de cache STABLE par société/utilisateur
  const sig = useMemo(
    () => stableSigFromSociete(infosSociete),
    [infosSociete?.id, infosSociete?.societe_id, infosSociete?.user_id, infosSociete?.siret]
  );

  const devisId = searchParams.get("devisId") || searchParams.get("devis");
  const factureIdParam = searchParams.get("factureId");

  // acompte/partielle params
  const initialValue = searchParams.get("value") ?? searchParams.get("acompteValue");
  const initialMode = searchParams.get("mode") ?? searchParams.get("acompteMode");

  /* ---------- state ---------- */
  const [devis, setDevis] = useState(null);
  const [dateEmis, setDateEmis] = useState(toYYYYMMDD());
  const [editDate, setEditDate] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [mode, setMode] = useState(initialMode || "percent");
  const [valeur, setValeur] = useState(initialValue ? parseFloat(initialValue) : 0);

  const [saving, setSaving] = useState(false);
  const [factureId, setFactureId] = useState(factureIdParam ? Number(factureIdParam) : null);
  const [numeroAssigned, setNumeroAssigned] = useState(null);
  const [error, setError] = useState("");
  const [introSafeHtml, setIntroSafeHtml] = useState("");

  // montants sauvegardés si édition
  const [savedHT, setSavedHT] = useState(null);
  const [savedTVA, setSavedTVA] = useState(null);
  const [savedTTC, setSavedTTC] = useState(null);
  const [savedRatioPct, setSavedRatioPct] = useState(null);
  // en haut, avec les autres états
  const [mentionsSafeHtml, setMentionsSafeHtml] = useState("");
  const [mentionsFontPx, setMentionsFontPx] = useState(12);


  const pdfRef = useRef(null);
  const footerRef = useRef(null);
  const [footerMm, setFooterMm] = useState(18);        // ~18mm par défaut

  // in AfficherDevis + FactureFromDevisBase (after refs are ready)
  useEffect(() => {
    const apply = () => {
      if (!footerRef.current || !pdfRef.current) return;
      const h = footerRef.current.offsetHeight || 0;
      pdfRef.current.style.setProperty("--footer-h", `${h}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    if (footerRef.current) ro.observe(footerRef.current);
    window.addEventListener("resize", apply);
    return () => { ro.disconnect(); window.removeEventListener("resize", apply); };
  }, [mentionsSafeHtml]);



  useEffect(() => {
    (async () => {
      const clean = DOMPurify.sanitize(devis?.intro_texte || "");
      const hydrated = await hydrateProtectedHtml(clean, sig);
      setIntroSafeHtml(hydrated);
    })();
  }, [devis?.intro_texte, sig]);

  // Purge (blob:) UNIQUEMENT quand on change d’utilisateur / société
  useEffect(() => {
    revokeAllProtectedUrls();
  }, [sig]);

  /* ---------- chargements ---------- */
  useEffect(() => {
    if (!devisId) return;
    (async () => {
      try {
        const { data } = await axiosInstance.get(`/devis/${devisId}`);
        setDevis(data);
      } catch (e) {
        console.error("Erreur chargement devis:", e);
        setError("Impossible de charger le devis.");
        notify.error({ title: "Chargement échoué", message: "Impossible de charger le devis." });
      }
    })();
  }, [devisId]);

  // Mentions facturation (fallback sur mentions légales)
  useEffect(() => {
    (async () => {
      try {
        let data;
        try {
          data = (await axiosInstance.get("/mentionsfacturation")).data || {};
        } catch {
          data = (await axiosInstance.get("/mentionslegales")).data || {};
        }

        const raw =
          data?.mentions_facturation ??
          data?.mentions_supplementaires_facture ??
          data?.mentions_supplementaires ??
          "";

        const clean = DOMPurify.sanitize((raw || "").trim());
        const hydrated = await hydrateProtectedHtml(clean, sig);
        setMentionsSafeHtml(hydrated.replace(/<\/?p[^>]*>/gi, ""));

        const px = Number(data?.mentions_font_px);
        setMentionsFontPx(Number.isFinite(px) ? px : 12);
      } catch {
        setMentionsSafeHtml("");
        setMentionsFontPx(12);
      }
    })();
  }, [sig]);

  // Édition
  useEffect(() => {
    const id = Number(factureIdParam);
    if (!id) return;
    (async () => {
      try {
        const { data } = await axiosInstance.get(`/factures/${id}`);
        setFactureId(id);
        setNumeroAssigned(data.numero || data.numero_facture || null);
        setDateEmis(toYYYYMMDD(data.date || data.date_facture || data.created_at || new Date()));

        setSavedHT(r2(data.total_ht || 0));
        setSavedTVA(r2(data.total_tva || 0));
        setSavedTTC(r2(data.total_ttc || data.montant_ttc || 0));

        if (type !== "finale") {
          if (data.mode_calcul) setMode(String(data.mode_calcul));
          if (typeof data.valeur !== "undefined") setValeur(Number(data.valeur));
        }

        const meta = typeof data.meta === "string" ? safeJSON(data.meta, {}) : data.meta || {};
        let prc = null;
        if (typeof meta.ratio === "number") prc = meta.ratio;
        else if (typeof meta.ratio === "string") {
          const n = Number(meta.ratio);
          if (!Number.isNaN(n)) prc = n;
        }

        if (prc == null && (data.total_ttc || data.montant_ttc)) {
          const baseTTC = Number(meta?.base?.totalTTC || 0);
          const saved = Number(data.total_ttc || data.montant_ttc || 0);
          if (baseTTC > 0 && saved >= 0) prc = (saved / baseTTC) * 100;
        }
        if (prc != null) setSavedRatioPct(prc);
      } catch (e) {
        console.error("Erreur chargement facture:", e);
        setError("Impossible de charger la facture.");
      }
    })();
  }, [factureIdParam, type]);

  /* ---------- base devis ---------- */
  const lignes = useMemo(() => {
    if (!devis) return [];
    try {
      return Array.isArray(devis.contenu) ? devis.contenu : JSON.parse(devis.contenu || "[]");
    } catch {
      return [];
    }
  }, [devis]);

  const base = useMemo(() => {
    const totalHT = lignes.reduce((a, l) => a + totalLigne(l), 0);
    const totalTVA = lignes.reduce(
      (a, l) => a + totalLigne(l) * (parseFloat(l.tva || 0) / 100),
      0
    );
    const totalTTC = totalHT + totalTVA;
    return { totalHT: r2(totalHT), totalTVA: r2(totalTVA), totalTTC: r2(totalTTC) };
  }, [lignes]);

  /* ---------- factures déjà liées ---------- */
  const [existingOnDevis, setExistingOnDevis] = useState([]);
  useEffect(() => {
    if (!devisId) return;
    let alive = true;
    const idNum = Number(devisId);

    const tries = [
      { url: "/factures", params: { devis_id: idNum } },
      { url: "/factures", params: { devisId: idNum } },
      { url: `/devis/${idNum}/factures`, params: null },
      { url: "/factures", params: null },
    ];

    const toN = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const belongsToDevis = (f) => {
      const meta = safeJSON(f.meta, {});
      const pool = [f.devis_id, f.devisId, f.devis, meta?.devis_id, meta?.source_devis_id]
        .map(toN)
        .filter((x) => x != null);
      return pool.length ? pool.some((x) => x === idNum) : true;
    };

    (async () => {
      let collected = [];
      for (const t of tries) {
        try {
          const { data } = await axiosInstance.get(
            t.url,
            t.params ? { params: t.params } : undefined
          );
          const arr = Array.isArray(data) ? data : [];
          if (arr.length) {
            collected = arr;
            if (t !== tries[tries.length - 1]) break;
          }
        } catch { }
      }
      const filtered = (collected || []).filter(belongsToDevis);
      if (alive) setExistingOnDevis(filtered);
    })();

    return () => {
      alive = false;
    };
  }, [devisId]);

  const consumedTTC = useMemo(() => {
    if (!Array.isArray(existingOnDevis)) return 0;
    return r2(
      existingOnDevis
        .filter((f) =>
          ["acompte", "partielle"].includes(
            String(f.type || f.type_facture || "").toLowerCase()
          )
        )
        .reduce((s, f) => s + Number(f.total_ttc || f.montant_ttc || 0), 0)
    );
  }, [existingOnDevis]);

  /* ---------- montants ---------- */
  const ratioPercentAP =
    type === "acompte" || type === "partielle"
      ? mode === "percent"
        ? Math.max(0, Math.min(100, Number(valeur || 0)))
        : mode === "ht"
          ? base.totalHT
            ? (Math.min(Number(valeur || 0), base.totalHT) / base.totalHT) * 100
            : 0
          : base.totalTTC
            ? (Math.min(Number(valeur || 0), base.totalTTC) / base.totalTTC) * 100
            : 0
      : 0;

  const finaleRemainingTTC =
    type === "finale" ? r2(Math.max(0, base.totalTTC - consumedTTC)) : 0;
  const ratioPercentFinale =
    type === "finale" && base.totalTTC > 0 ? r2((finaleRemainingTTC / base.totalTTC) * 100) : 0;

  const ratioPercent = type === "finale" ? ratioPercentFinale : ratioPercentAP;
  const ratio = ratioPercent / 100;

  const calcHT = r2(base.totalHT * ratio);
  const calcTVA = r2(base.totalTVA * ratio);
  const calcTTC = r2(calcHT + calcTVA);

  const isEdition = Boolean(factureId && savedTTC !== null);

  const partHT = isEdition ? savedHT : calcHT;
  const partTVA = isEdition ? savedTVA : calcTVA;
  const partTTC = isEdition ? savedTTC : calcTTC;

  const ratioPctForText = isEdition && savedRatioPct != null ? savedRatioPct : ratioPercent;
  // === Snapshot état initial (APRÈS partHT/TVA/TTC) ===
  const initialSnapshotRef = useRef(null);

  const currentSnapshot = useMemo(
    () => ({
      devisId: Number(devisId) || null,
      type: String(type || ""),
      dateEmis: String(dateEmis || ""),
      mode: type === "finale" ? "reste" : String(mode || "percent"),
      valeur: type === "finale" ? null : Number(valeur || 0),
      partHT,
      partTVA,
      partTTC,
    }),
    [devisId, type, dateEmis, mode, valeur, partHT, partTVA, partTTC]
  );

  useEffect(() => {
    // on prend un snapshot quand on charge un devis/facture
    initialSnapshotRef.current = JSON.stringify(currentSnapshot);
  }, [devis?.id, factureId, numeroAssigned]);

  const isDirty = useMemo(() => {
    const a = initialSnapshotRef.current || "";
    const b = JSON.stringify(currentSnapshot);
    return a !== b;
  }, [currentSnapshot]);

  // utilisé par le guard (définition plus bas de handleSave)
  const saveForGuard = useCallback(async () => {
    if (saving) return;
    await handleSave();
  }, [saving]); // (pas de dépendance circulaire sur handleSave)

  // hook "quitter sans enregistrer"
  const { safeNavigate } = useUnsavedChanges({
    isDirty,
    onSave: saveForGuard,
    navigate,
    askConfirm,
  });

  // ---- Échéancier ----
  const terms = useMemo(() => parsePaymentTerms(mentionsSafeHtml), [mentionsSafeHtml]);
  const echeanceFactureISO = computeDueDateISO(dateEmis, terms);
  const paymentLeadLabel = terms.aReception
    ? "à réception"
    : terms.finDeMois
      ? `${terms.days} jours fin de mois`
      : `${terms.days} jours`;

  /* ---------- bloc texte société + logos ---------- */
  const rawBloc = (infosSociete?.bloc_html || "").trim();
  const [blocHtmlSafe, setBlocHtmlSafe] = useState("");

  useEffect(() => {
    (async () => {
      const clean = DOMPurify.sanitize(infosSociete?.bloc_html?.trim() || "");
      const hydrated = await hydrateProtectedHtml(clean, sig);
      setBlocHtmlSafe(hydrated);
    })();
  }, [infosSociete?.bloc_html, sig]);

  // Texte société (px)
  const posTexteRaw = safeJSON(infosSociete?.positiontexte, infosSociete?.positiontexte);
  const posTexte = {
    x: pxNum(posTexteRaw?.x, 40),
    y: pxNum(posTexteRaw?.y, 120),
    width: pxNum(posTexteRaw?.width, 320),
    height: pxNum(posTexteRaw?.height, null),
  };
  const getTexteStyle = () => ({
    position: "absolute",
    transform: `translate(${posTexte.x}px, ${posTexte.y}px)`,
    width: posTexte.width ? `${posTexte.width}px` : undefined,
    height: posTexte.height ? `${posTexte.height}px` : "auto",
    zIndex: 25,
  });

  // Logos
  const logosOnPage = useMemo(() => {
    const arr = safeJSON(infosSociete?.logos_on_page, infosSociete?.logos_on_page);
    if (Array.isArray(arr) && arr.length) {
      return arr.map((p, i) => ({
        id: p.id || `pl-${i}`,
        libId: p.libId || `lib-${i}`,
        src: p.src || (p.file_id ? `/api/files/${p.file_id}` : ""),
        x: pxNum(p?.x, 420),
        y: pxNum(p?.y, 40),
        width: pxNum(p?.width, 140),
        height: pxNum(p?.height, 70) || 70,
      }));
    }
    const p = safeJSON(infosSociete?.positionlogo, infosSociete?.positionlogo);
    if (infosSociete?.logo && p) {
      return [
        {
          id: "pl-fallback",
          libId: "lib-0",
          src: infosSociete.logo,
          x: pxNum(p?.x, 420),
          y: pxNum(p?.y, 40),
          width: pxNum(p?.width, 140),
          height: pxNum(p?.height, 70) || 70,
        },
      ];
    }
    return [];
  }, [infosSociete?.logos_on_page, infosSociete?.positionlogo, infosSociete?.logo, sig]);

  /* ---------- export PDF ---------- */
  const exportPDF = async () => {
    try {
      notify.info({ title: "Préparation", message: "Génération du PDF…" });

      const rootEl = pdfRef.current;
      const fEl = footerRef.current;
      if (rootEl && fEl) {
        const h = fEl.offsetHeight || 0;
        rootEl.style.setProperty("--footer-h", `${h}px`);
      }
      rootEl.classList.add('pdf-export');        // ← active la reservation
      await new Promise(r => requestAnimationFrame(r)); // layout stable

      const filename = numeroAssigned
        ? `facture-${type}-${numeroAssigned}.pdf`
        : `facture-${type}-${toYYYYMMDD()}.pdf`;

      const blob = await buildPdfBlobWithLastFooterAndPages(rootEl, fEl, { filename });

      rootEl.classList.remove('pdf-export');     // nettoyage

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);

      notify.success({ title: "PDF prêt", message: "La facture a été exportée." });
    } catch (e) {
      pdfRef.current?.classList?.remove?.('pdf-export');
      notify.error({ title: "Échec export PDF", message: "La génération du PDF a échoué." });
    }
  };



  /* ---------- save ---------- */
  /* ---------- save ---------- */
  const handleSave = useCallback(async () => {
    if (!devisId || !devis?.client_id) {
      alert("Devis ou client manquant.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      notify.info({ title: "Enregistrement", message: "Sauvegarde en cours…" });

      const ratioText = pct(isEdition && savedRatioPct != null ? savedRatioPct : ratioPercent);

      const label =
        type === "finale"
          ? `Facture finale (reste à payer) pour le devis ${devis.numero_devis || devis.numero}`
          : `${type === "acompte" ? "Acompte" : "Facturation partielle"}${mode === "percent" ? ` (${ratioText} %)` : ""
          } pour le devis ${devis.numero_devis || devis.numero}`;

      const contenu = JSON.stringify([{ type: "note", designation: label }]);

      const payload = {
        type,
        devis_id: Number(devisId),
        client_id: Number(devis.client_id),
        date: dateEmis,
        date_facture: dateEmis,
        total_ht: partHT,
        total_tva: partTVA,
        total_ttc: partTTC,
        montant_ht: partHT,
        montant_tva: partTVA,
        montant_ttc: partTTC,
        mode_calcul: type === "finale" ? "reste" : mode,
        valeur: type === "finale" ? undefined : Number(valeur || 0),
        meta: JSON.stringify({
          ratio: Number(
            (isEdition && savedRatioPct != null ? savedRatioPct : ratioPercent).toFixed(4)
          ),
          base: { totalHT: base.totalHT, totalTVA: base.totalTVA, totalTTC: base.totalTTC },
          consumedTTC,
          kind: type,
          devisRef: devis.numero_devis || devis.numero || devis.id,
        }),
        contenu,
        intro_texte: "",
        projet_nom:
          devis.projet_nom ||
          devis.projet ||
          devis.titre_projet ||
          `${devis.client_nom ? devis.client_nom + " — " : ""}Devis ${devis.numero_devis || devis.numero || devis.id
          }`,
      };

      let id = factureId;

      if (id) {
        const { data } = await axiosInstance.put(`/factures/${id}`, payload, {
          headers: { "Content-Type": "application/json" },
        });
        setNumeroAssigned(data?.numero || data?.numero_facture || numeroAssigned);
        notify.success({
          title: "Mise à jour",
          message: `Facture ${data?.numero || data?.numero_facture || ""} mise à jour.`,
        });
      } else {
        const { data } = await axiosInstance.post(`/factures`, payload, {
          headers: { "Content-Type": "application/json" },
        });
        id = data?.id;
        setFactureId(id || null);
        setNumeroAssigned(data?.numero || data?.numero_facture || null);
        notify.success({
          title: "Enregistrée",
          message: `Facture ${data?.numero || data?.numero_facture || ""} sauvegardée.`,
        });
      }

      // reset du snapshot après save OK
      initialSnapshotRef.current = JSON.stringify(currentSnapshot);

      localStorage.setItem("docs:refresh", String(Date.now()));
    } catch (e) {
      console.error("Erreur enregistrement facture :", e);
      setError(e?.response?.data?.message || "Erreur lors de l'enregistrement");
      notify.error({ title: "Échec", message: "L’enregistrement a échoué." });
    } finally {
      setSaving(false);
    }
  }, [
    devisId,
    devis,
    dateEmis,
    partHT,
    partTVA,
    partTTC,
    mode,
    valeur,
    type,
    ratioPercent,
    savedRatioPct,
    base.totalHT,
    base.totalTVA,
    base.totalTTC,
    consumedTTC,
    factureId,
    numeroAssigned,
    currentSnapshot,
  ]);

  function MentionsBlock({ html, fontPx = 12 }) {
    if (!html) return null;
    return (
      <div
        className="mentions-legales"
        style={{ fontSize: `${fontPx}px`, lineHeight: 1.5 }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }
  /* ---------- UI ---------- */
  if (error) return <div className="p-6 text-center text-red-600">{error}</div>;
  if (!devis) return <div className="p-6 text-center">Chargement…</div>;

  const { ligne1: addr1, cp, ville } = splitClientAddr(devis);
  const cpVille = [cp, ville].filter(Boolean).join(" ").trim();

  const title =
    type === "finale"
      ? `Facture finale ${numeroAssigned ? `n° ${numeroAssigned}` : "(n° à l’enregistrement)"}`
      : `${type === "acompte" ? "Facture d’acompte" : "Facture partielle"
      } ${numeroAssigned ? `n° ${numeroAssigned}` : "(n° à l’enregistrement)"}`;

  return (
    <div className="p-4 space-y-4">
      <style>{`
  /* Même logique que FactureFinale / AfficherDevis */
  .a4-page{
    position:relative;
    width:210mm;
    min-height:297mm;
    background:#fff;
  }

  /* Contenu décalé vers le bas + réserve dynamique pour le footer */
  .a4-content{
    margin-top:320px;              /* même offset que Final/Fichier */
    padding:0 15mm;
    padding-bottom: calc((var(--footer-h, 120px) * 1.3) + 24px);
  }

  /* Footer collé en bas, par-dessus le contenu (qui le “pousse” via padding-bottom) */
  .a4-footer{
    position:absolute;
    left:0;
    right:0;
    bottom:0;
    padding:8mm 10mm 10mm;
    text-align:center;
    page-break-inside:avoid;
  }

  /* Barre d’actions (comme AfficherDevis / FactureFinale) */
  .actions-bar :is(button, a){
    padding:10px 16px;
    line-height:1.2;
    font-size:.875rem;
    border-radius:9999px;
  }

  /* Masquer l'UI uniquement pendant l'export */
  .a4-page.pdf-export .no-print,
  .a4-page.pdf-export .no-print *{
    display:none!important;
    visibility:hidden!important;
  }
`}</style>


      <div className="actions-bar flex flex-wrap justify-center gap-2 mb-4">
        <ActionsEmailFactureLikeDevis
          factureMeta={{
            id: factureId || null,
            numero: numeroAssigned || "",
            client_email: devis?.client_email || "",
            societe_nom: infosSociete?.nom || infosSociete?.societe_nom || "FlowDevis",
          }}
          pdfElRef={pdfRef}
          footerElRef={footerRef}
          kind={type}  // "acompte" | "partielle" | "finale"
        />

        <BoutonOnglet actif={false} onClick={exportPDF}>Exporter en PDF</BoutonOnglet>
        <BoutonOnglet actif={saving} onClick={handleSave}>{saving ? "Enregistrement…" : "Enregistrer"}</BoutonOnglet>
        <BoutonOnglet actif={false} onClick={() => safeNavigate("/documents")}>Retour aux documents</BoutonOnglet>
      </div>

      <div className="a4-page" ref={pdfRef}>
        {blocHtmlSafe && (
          <div style={getTexteStyle()}>
            <div
              className="ql-editor !p-0 !leading-snug"
              style={{ overflow: "visible", maxHeight: "none" }}
              dangerouslySetInnerHTML={{ __html: blocHtmlSafe }}
            />
          </div>
        )}

       {logosOnPage.map((pl) => {
  const rawSrc = pl.src || pl.url || "";

  const style = {
    transform: `translate(${pxNum(pl.x, 0)}px, ${pxNum(pl.y, 0)}px)`,
    width: `${pxNum(pl.width, 140)}px`,
    height: `${pxNum(pl.height, 70)}px`,
    zIndex: 20,
    pointerEvents: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  // Cas où pl.src serait un File (rare ici, mais on le gère proprement)
  const isFileObj =
    typeof rawSrc !== "string" &&
    typeof File !== "undefined" &&
    rawSrc instanceof File;

  let src = "";
  if (!isFileObj) {
    if (/^https?:\/\//i.test(rawSrc)) {
      // URL Cloudinary déjà complète
      src = rawSrc;
    } else {
      // ancien format: "55" ou "/api/files/55"
      const idMatch = String(rawSrc).match(/(\d+)$/);
      const id = idMatch ? idMatch[1] : null;
      if (id) {
        const BASE = (axiosInstance?.defaults?.baseURL || "").replace(/\/+$/, "");
        const ROOT = BASE.replace(/\/api\/?$/i, "");
        src = `${ROOT}/api/files/${id}`;
      }
    }
  }

  return (
    <div
      key={pl.id || src || Math.random()}
      className="absolute"
      style={style}
    >
      {isFileObj ? (
        <img
          src={URL.createObjectURL(rawSrc)}
          alt="Logo société"
          style={{ maxWidth: "100%", maxHeight: "100%", width: "auto", height: "auto" }}
        />
      ) : (
        src && (
          <img
            src={src}
            alt="Logo société"
            style={{ maxWidth: "100%", maxHeight: "100%", width: "auto", height: "auto" }}
          />
        )
      )}
    </div>
  );
})}

        {/* Cartouche client (à droite) */}
        <div className="absolute z-[110] top-[150px] right-[40px] min-w-[300px] max-w-[500px] bg-white border border-gray-300 shadow-md rounded p-4 text-sm">
          <h2 className="text-md font-semibold mb-2">{devis.client_nom || "—"}</h2>
          <div className="text-gray-700 space-y-1">
            {addr1 && <p>{addr1}</p>}
            {cpVille && <p>{cpVille}</p>}
          </div>
        </div>
        {leaveGuard.open && (
          <UnsavedDialog
            open={leaveGuard.open}
            onCancel={() => closeLeaveGuard("cancel")}
            onExit={() => closeLeaveGuard("leave")}
            onSaveAndExit={() => closeLeaveGuard("save")}
          />
        )}

        {/* Numéro + date (à gauche) */}
        <div
          className="absolute left-[40px] top-[280px] w-[340px] text-sm z-[40]"
          style={{ pointerEvents: "auto" }}
        >
          <div className="font-bold">{title}</div>
          <div className="mt-1 flex items-center gap-2">
            {exporting ? (
              <div className="text-xs text-gray-700">Émis le {dFR(dateEmis)}</div>
            ) : editDate ? (
              <input
                type="date"
                value={dateEmis}
                onChange={(e) => setDateEmis(e.target.value)}
                onBlur={() => setEditDate(false)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Escape") e.currentTarget.blur();
                }}
                autoFocus
                className="no-print border p-1 rounded text-xs w-full bg-white"
              />
            ) : (
              <>
                <div className="text-xs text-gray-700">Émis le {dFR(dateEmis)}</div>
                {
                  // ⛔️ Ne pas afficher en mode snapshot (PDF pour mail)
                  !(typeof document !== "undefined" &&
                    document.body.classList.contains("static-pdf")) && (
                    <button
                      type="button"
                      className="no-print text-xs text-sky-700 hover:underline"
                      onClick={() => setEditDate(true)}
                      title="Modifier la date"
                    >
                      Modifier
                    </button>
                  )
                }
              </>
            )}
          </div>
        </div>

        <div
          className="a4-content">
          {introSafeHtml && (
            <div className="text-sm italic text-gray-700 mb-4">
              <div className="ql-editor !p-0" dangerouslySetInnerHTML={{ __html: introSafeHtml }} />
            </div>
          )}

          <div className="text-sm">
            {devis.numero_devis && (
              <p className="font-semibold">
                {type === "finale" ? "Solde pour le devis" : "Pour le devis"}{" "}
                <strong>{devis.numero_devis}</strong>
                {devis.date_devis ? ` du ${dFR(devis.date_devis)}` : ""}.
              </p>
            )}

            {(type === "acompte" || type === "partielle") && (
              <p className="mt-1">
                Correspondant à <strong>{pct(ratioPctForText)} %</strong> du montant total de{" "}
                <strong>{fmt2(base.totalHT)} € HT</strong>.
              </p>
            )}

            <p className="mt-1">
              Montant {type === "finale" ? "à facturer" : "facturé"} :{" "}
              <strong>{toCurrency(partTTC)}</strong> TTC.
            </p>
          </div>

          <div className="mt-6 px-6 no-due-hint">
            <BlocTotaux
              sousTotalHT={null}
              remise={null}
              totalHT={partHT}
              tva={partTVA}
              totalTTC={partTTC}
              showDelaiEcheance={false}
            />
          </div>

          {mentionsSafeHtml && (
            <div className="mt-4 text-xs text-gray-700 leading-relaxed border-t pt-2 no-split">
              <p className="mb-2">
                <strong>Délai de paiement :</strong> {paymentLeadLabel} —{" "}
                <strong>Échéance le {dFR(echeanceFactureISO)}.</strong>
              </p>
              <MentionsBlock html={mentionsSafeHtml} fontPx={mentionsFontPx} />
            </div>
          )}

        </div>

        {/* Footer collé en bas, hors contenu */}
        <div className="a4-footer" ref={footerRef}>
          <FooterApercu />
        </div>
      </div>
    </div>
  );
};
